import React from 'react';

import { IContentType, Identifiable } from '../../../types/core';
import SeoStatusCell from './SeoStatusCell';

/**
 * Builds the shared "SEO" table column, so listings, destinations and posts get an
 * identical cell instead of three near-copies.
 *
 * `contentType` comes from the table's OPTIONS response rather than being hardcoded —
 * ContentType ids differ between environments, so hardcoding one would silently attach
 * metadata to the wrong model.
 */
export interface SeoColumnConfig<T> {
  /** Public path of the record's page, for the previews. */
  pathFor: (record: T) => string;
  /** Shown in the drawer title. */
  labelFor: (record: T) => string;
  /** Image the page falls back to when no share image is set. */
  imageFor?: (record: T) => string | null | undefined;
  /** Gallery the share-image picker browses. Defaults to the blog media gallery. */
  galleryId?: number;
}

export const seoColumn = <T extends Identifiable>(config: SeoColumnConfig<T>) => ({
  key: 'seo',
  title: 'SEO',
  width: 190,
  render: (record: T, contentType?: IContentType) => (
    <SeoStatusCell
      contentTypeId={contentType?.id}
      objectId={record.id}
      path={config.pathFor(record)}
      recordLabel={config.labelFor(record)}
      fallbackImageUrl={config.imageFor?.(record)}
      galleryId={config.galleryId}
    />
  ),
});

/**
 * Same column, for tables that build their own columns instead of using `RecordTable`
 * (the main Listings page) and so have to supply the ContentType themselves.
 */
export const seoColumnFor = <T extends Identifiable>(
  config: SeoColumnConfig<T>,
  contentType?: IContentType,
) => {
  const { render, ...column } = seoColumn<T>(config);
  return { ...column, render: (record: T) => render(record, contentType) };
};

/** Gallery used by the destination image pickers elsewhere in this admin. */
export const DESTINATION_GALLERY_ID = 228;
